import React, { useState, useEffect } from 'react';
import './DashboardPage.css';
import { useAuth } from './AuthContext';
import confetti from 'canvas-confetti';

interface DashboardCounts {
  directReferrals: number;
  walletBalance: number;
  selfCount: number;
  selfFromDirectCount: number;
  targetName: string | null;
  totalMemberscount: number;
  totalDirectIdCreationDebit: number;
  totalDirectCredit: number;
  yesterdaySelfIdCount: number;
  totalDirectSelfCredit: number;
  totalSelfIdCreationDebit: number;
  totalAutofillCredit: number;
  totalCreditSum: number;
}
interface Member {
  id: number;
  member_id: string;
  sponsor_id: string | null;
  joining_date: string | null;
  level: string;
  nxtlevelcount: number | 'N/A' | 'completed';
}

const DashboardPage: React.FC = () => {
  const { user } = useAuth();
  // State for dashboard stats
  const [counts, setCounts] = useState<DashboardCounts | null>(null);
  // State for members table
  const [members, setMembers] = useState<Member[]>([]);
  // State management for different parts of the page
  const [isStatsLoading, setIsStatsLoading] = useState(true);
  const [statsError, setStatsError] = useState<string | null>(null);
  const [isMembersLoading, setIsMembersLoading] = useState(true);
  const [membersError, setMembersError] = useState<string | null>(null);

  // Pagination state for the members table
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [goToPage, setGoToPage] = useState('');
  const [itemsPerPage, setItemsPerPage] = useState(20);
  const [searchTerm, setSearchTerm] = useState('');
  const [searchInput, setSearchInput] = useState('');
  
  // Admin "View As" state
  const [viewAsUser, setViewAsUser] = useState<string>('');
  const [viewAsSearchTerm, setViewAsSearchTerm] = useState<string>('');
  const [verificationMessage, setVerificationMessage] = useState<{ status: 'success' | 'error'; message: string } | null>(null);
  const [showYesterdayModal, setShowYesterdayModal] = useState(false);

  useEffect(() => {
    if (showYesterdayModal && counts && counts.yesterdaySelfIdCount > 0) {
      confetti({
        particleCount: 150,
        spread: 70,
        origin: { y: 0.6 },
        zIndex: 2000, // Ensure it appears above the modal (z-index 1000)
      });
    }
  }, [showYesterdayModal, counts]);

  useEffect(() => {
    const fetchDashboardData = async () => {
      setIsStatsLoading(true);
      setStatsError(null);
      try {
        // Fetch both counts and members data in parallel
        const params = new URLSearchParams();
        if (user?.role === 'admin' && viewAsUser) {
          params.append('username', viewAsUser);
        }
        
        const [countsResponse, summaryResponse] = await Promise.all([
          fetch(`/api/dashboard/counts?${params.toString()}`, { credentials: 'include' }),
          fetch(`/api/wallet/summary?${params.toString()}`, { credentials: 'include' })
        ]);
        
        if (!countsResponse.ok) {
          const errorData = await countsResponse.json();
          if (countsResponse.status === 404) {
            setCounts(null); // Explicitly set to null if user not found
            setStatsError(`User "${viewAsUser}" not found.`);
            return; // Stop execution if the primary user data is not found
          }
          throw new Error(errorData.message || 'Failed to fetch dashboard counts');
        }

        if (!summaryResponse.ok) {
          const errorData = await summaryResponse.json();
          throw new Error(errorData.message || 'Failed to fetch wallet summary');
        }

        const countsData: DashboardCounts = await countsResponse.json();
        const summaryData = await summaryResponse.json();

        const defaultCounts: DashboardCounts = {
          directReferrals: 0,
          walletBalance: 0,
          selfCount: 0,
          selfFromDirectCount: 0,
          targetName: null,
          totalMemberscount: 0,
          totalDirectIdCreationDebit: 0,
          totalDirectCredit: 0,
          yesterdaySelfIdCount: 0,
          totalDirectSelfCredit: 0,
          totalSelfIdCreationDebit: 0,
          totalAutofillCredit: 0,
          totalCreditSum: 0,
        };

        setCounts({ ...defaultCounts, ...countsData, ...summaryData.summary });

        if (!viewAsUser) {
          const hasShown = sessionStorage.getItem('yesterdayModalShown');
          if (!hasShown) {
            setShowYesterdayModal(true);
            sessionStorage.setItem('yesterdayModalShown', 'true');
          }
        }

      } catch (err) {
        if (err instanceof Error) {
          setStatsError(err.message);
        } else {
          setStatsError('An unknown error occurred while fetching stats.');
        }
      } finally {
        setIsStatsLoading(false);
      }
    };

    fetchDashboardData();
  }, [viewAsUser, user?.role]);

  useEffect(() => {
    const fetchMembers = async () => {
      setIsMembersLoading(true);
      setMembersError(null);
      try {
        const params = new URLSearchParams({
          page: String(currentPage),
          pageSize: String(itemsPerPage),
          search: searchTerm,
        });
        if (user?.role === 'admin' && viewAsUser) {
          params.append('username', viewAsUser);
        }
        const membersResponse = await fetch(`/api/dashboard/members?${params.toString()}`, { credentials: 'include' });
        if (!membersResponse.ok) {
          const errorData = await membersResponse.json();
          throw new Error(errorData.message || 'Failed to fetch members');
        }
        const membersPayload = await membersResponse.json();
        setMembers(membersPayload.data);
        setTotalPages(membersPayload.pagination.totalPages);
      } catch (err) {
        if (err instanceof Error) {
          setMembersError(err.message);
        } else {
          setMembersError('An unknown error occurred while fetching members.');
        }
      } finally {
        setIsMembersLoading(false);
      }
    };

    fetchMembers();
  }, [currentPage, itemsPerPage, searchTerm, viewAsUser, user?.role]);

  // Reset to page 1 when filters or items per page change
  useEffect(() => {
    setCurrentPage(1);
  }, [itemsPerPage, searchTerm, viewAsUser]);

  const handlePageChange = (newPage: number) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setCurrentPage(newPage);
    }
  };

  const handleGoToPageSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const page = parseInt(goToPage, 10);
    if (!isNaN(page)) {
      handlePageChange(page);
    }
    setGoToPage('');
  };

  const handleSearchSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setSearchTerm(searchInput);
  };

  const handleViewAsSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const usernameToView = viewAsSearchTerm.trim();
    if (!usernameToView) {
      setVerificationMessage({ status: 'error', message: 'Please enter a username to view.' });
      return;
    }

    // Reset previous messages and user view
    setVerificationMessage(null);

    try {
      // 1. Verify user exists
      const response = await fetch(`/api/profile?username=${encodeURIComponent(usernameToView)}`, {
        credentials: 'include',
      });
      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.message || 'User not found.');
      }
      // 2. If successful, switch the view
      setViewAsUser(usernameToView);
    } catch (err) {
      // 3. If verification fails, show an error and clear any existing "view as" user
      const errorMessage = err instanceof Error ? err.message : 'An unknown error occurred.';
      setVerificationMessage({ status: 'error', message: errorMessage });
      setViewAsUser('');
    }
  };

  const handleClearViewAs = () => {
    setViewAsUser('');
    setViewAsSearchTerm('');
    setVerificationMessage(null);
    setStatsError(null);
  };


  const renderSelfMembersTable = () => {
    if (isMembersLoading) return (
      <div className="self-members-card">
        <h3>IDs AutoFill</h3>
        <p>Loading list...</p>
      </div>
    );

    if (membersError) return (
      <div className="self-members-card error-message">
        <h3>IDs AutoFill</h3>
        <p>{membersError}</p>
      </div>
    );

    return (
      <div className="self-members-card">
        <h3 className="table-header-with-count">
          <span className="header-title">IDs AutoFill</span>
          {counts && <span className="table-income">Autofill Income: <strong>₹{counts.totalAutofillCredit.toFixed(2)}</strong></span>}
          <form onSubmit={handleSearchSubmit} className="header-search-form">
            <input
              type="text"
              placeholder="Search by Member ID..."
              value={searchInput}
              onChange={e => setSearchInput(e.target.value)}
              className="search-input header-search-input"
            />
            <button type="submit" className="search-button">Search</button>
          </form>
          <div className="header-stats">
            {counts && <span className="table-count">Total IDs: {counts.totalMemberscount}</span>}
          </div>
        </h3>
        {members.length > 0 ? (
          <div className="table-responsive">
            <table className="self-members-table">
              <thead>
                <tr>
                  <th>S.No.</th>
                  <th>ID</th>
                  <th>Member ID</th>
                  <th>Level</th>
                  <th>Next Level Count</th>
                  <th>Date Joined</th>
                </tr>
              </thead>
              <tbody>
                {members.map((member, index) => {
                  const levelNumber = parseFloat(member.level);
                  const isComplete = member.level === 'completed' || levelNumber >= 10;

                  return (
                    <tr key={member.id}>
                      <td data-label="S.No.">{(currentPage - 1) * itemsPerPage + index + 1}</td>
                      <td data-label="ID">{member.id}</td>
                      <td data-label="Member ID">{member.member_id}</td>
                      <td data-label="Level" className={isComplete ? 'level-complete' : ''}>{member.level}</td>
                      <td data-label="Next Level Count" className={isComplete ? 'level-complete' : ''}>{member.nxtlevelcount}</td>
                      <td data-label="Date Joined">{member.joining_date ? new Date(member.joining_date).toLocaleDateString() : 'N/A'}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            {members.length > 0 && (
              <div className="pagination-controls">
                <button onClick={() => handlePageChange(1)} disabled={currentPage === 1}>
                  &laquo; First
                </button>
                <button onClick={() => handlePageChange(currentPage - 1)} disabled={currentPage === 1}>
                  &lsaquo; Previous
                </button>
                <span className="page-info">Page {currentPage} of {totalPages}</span>
                <button onClick={() => handlePageChange(currentPage + 1)} disabled={currentPage === totalPages}>
                  Next &rsaquo;
                </button>
                <button onClick={() => handlePageChange(totalPages)} disabled={currentPage === totalPages}>
                  Last &raquo;
                </button>
                <form onSubmit={handleGoToPageSubmit} className="go-to-page-form">
                  <input
                    type="number"
                    value={goToPage}
                    onChange={(e) => setGoToPage(e.target.value)}
                    className="page-input"
                    min="1"
                    max={totalPages}
                    placeholder="Page"
                  />
                  <button type="submit">Go</button>
                </form>
                <div className="items-per-page-selector">
                  <select value={itemsPerPage} onChange={e => setItemsPerPage(Number(e.target.value))} className="filter-select">
                    <option value={20}>20</option>
                    <option value={50}>50</option>
                    <option value={100}>100</option>
                    <option value={200}>200</option>
                    <option value={500}>500</option>
                    <option value={1000}>1000</option>
                  </select>
                </div>
              </div>
            )}
          </div>
        ) : (
          <p>You have not registered any self IDs yet.</p>
        )}
      </div>
    );
  };

  const renderStats = () => {
    if (isStatsLoading) return <p>Loading stats...</p>;
    if (statsError && !counts) return <div className="error-message">{statsError}</div>;
    if (counts) {
      return (
        <div className="stats-grid">
          <div className="stat-card wallet-card">
            <div className="stat-icon">💰</div>
            <h3>Wallet Balance</h3>
            <p className="stat-number">₹{counts.walletBalance.toFixed(2)}</p>           
             <small>
                 {counts.totalCreditSum >= 1000
                  ? `${(counts.totalCreditSum / 1000).toFixed(3)} L Oil Earned`
                  : `${counts.totalCreditSum} ml Oil Earned`}
            </small>
          </div>
          <div className="stat-card direct-card">
            <div className="stat-icon">👥</div>
            <h3>Direct</h3>
            <p className="stat-number">{counts.directReferrals}</p>
            <small>Direct Referrals</small>
            <div className="stat-corner-amount">
              <div className="oil-earn">
                ₹{(counts.totalDirectCredit).toFixed(2)} earned<br />
                <span className="spent-text">
                  ₹{(counts.totalDirectIdCreationDebit).toFixed(2)} spent
                </span>
              </div>
            </div>
          </div>
          <div className="stat-card self-from-direct-card">
            <div className="stat-icon">👨‍👩‍👧‍👦</div>
            <h3>Self from Direct</h3>
            <p className="stat-number">{counts.selfFromDirectCount}</p>
            <small>IDs from your directs</small>
            <div className="stat-corner-amount">
              <div className="oil-earn">
                ₹{(counts.totalDirectSelfCredit).toFixed(2)} earned
              </div>
            </div>
          </div>
          <div className="stat-card self-card">
            <div className="stat-icon">👤</div>
            <h3>Self</h3>
            <p className="stat-number">{counts.selfCount}</p>
            <small>Self Registrations</small>
            <div className="stat-corner-amount">
              <div className="oil-earn">
                <span className="spent-text">
                  ₹{counts.totalSelfIdCreationDebit.toFixed(2)} spent
                </span><br />
              </div>
            </div>
          </div>
        </div>
      );
    }
    return null;
  };

  return (
    <div className="dashboard-container">
      {/* Component-specific styles for the admin view controls */}
      <style>{`
        .spent-text {
          color: #d9534f; /* A soft red color for debits/spent amounts */
        }
        .admin-view-controls {
          background-color: #e9ecef;
          padding: 15px;
          border-radius: 8px;
          margin-bottom: 20px;
        }
        .view-as-form {
          display: flex;
          gap: 10px;
          align-items: center;
        }
        .view-as-input {
          flex-grow: 1;
          border: 1px solid #ced4da;
          border-radius: 4px;
          padding: 10px 15px;
          font-size: 1rem;
        }
        .view-as-button {
          border: none;
          border-radius: 4px;
          padding: 10px 20px;
          font-size: 1rem;
          font-weight: 600;
          cursor: pointer;
          background-color: #007bff;
          color: white;
          transition: background-color 0.2s;
        }
        .view-as-button.clear-view-button {
          background-color: #6c757d;
        }
        .view-as-button:hover {
          opacity: 0.9;
        }
        .verification-message {
          padding: 8px 12px;
          border-radius: 4px;
          font-size: 0.9rem;
          font-weight: 500;
        }
        .verification-message.success {
          background-color: #d4edda; color: #155724;
        }
        .verification-message.error {
          background-color: #f8d7da; color: #721c24;
        }
        @media (max-width: 768px) {
          .view-as-form {
            flex-wrap: nowrap;
          }
        }
        .modal-overlay {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background-color: rgba(0, 0, 0, 0.6);
          backdrop-filter: blur(5px);
          display: flex;
          justify-content: center;
          align-items: center;
          z-index: 1000;
          animation: fadeIn 0.3s ease-out;
        }
        .modal-content {
          background: white;
          padding: 40px;
          border-radius: 20px;
          width: 90%;
          max-width: 350px;
          text-align: center;
          box-shadow: 0 20px 60px rgba(0, 0, 0, 0.2);
          animation: slideUp 0.4s cubic-bezier(0.16, 1, 0.3, 1);
        }
        .modal-title {
          font-size: 1.2rem;
          font-weight: 800;
          color: #0056b3;
          background-color: #e7f1ff;
          padding: 8px 16px;
          border-radius: 30px;
          display: inline-block;
          margin-bottom: 15px;
          text-transform: uppercase;
          letter-spacing: 1px;
        }
        .modal-count {
          font-size: 5rem;
          font-weight: 800;
          color: #007bff;
          line-height: 1;
          margin: 10px 0 30px;
          text-shadow: 4px 4px 0px rgba(0,123,255,0.1);
        }
        .modal-close-btn {
          background-color: #007bff;
          color: white;
          border: none;
          padding: 12px 35px;
          border-radius: 50px;
          cursor: pointer;
          font-size: 1.1rem;
          font-weight: 600;
          box-shadow: 0 5px 15px rgba(0, 123, 255, 0.3);
          transition: transform 0.2s, box-shadow 0.2s;
        }
        .modal-close-btn:hover {
          transform: translateY(-2px);
          box-shadow: 0 8px 20px rgba(0, 123, 255, 0.4);
        }
        @keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }
        @keyframes slideUp { from { transform: translateY(20px); opacity: 0; } to { transform: translateY(0); opacity: 1; } }
      `}</style>
      {showYesterdayModal && counts && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-title">Yesterday's Self IDs</div>
            <div className="modal-count">{counts.yesterdaySelfIdCount}</div>
            <button onClick={() => setShowYesterdayModal(false)} className="modal-close-btn">Awesome</button>
          </div>
        </div>
      )}
      {user?.role === 'admin' && (
        <div className="admin-view-controls">
          <form onSubmit={handleViewAsSubmit} className="view-as-form">
            <input
              type="text"
              value={viewAsSearchTerm}
              onChange={(e) => {
                setViewAsSearchTerm(e.target.value);
                setVerificationMessage(null); // Reset verification on change
              }}
              placeholder="Enter username to view their dashboard"
              className="view-as-input"
            />
            <button type="submit" className="view-as-button">View Dashboard</button>
            {viewAsUser && (
              <button type="button" onClick={handleClearViewAs} className="view-as-button clear-view-button">
                View My Dashboard
              </button>
            )}
          </form>
          {verificationMessage && (
            <div className={`verification-message ${verificationMessage.status}`} style={{marginTop: '10px', textAlign: 'left'}}>
              {verificationMessage.message}
            </div>
          )}
        </div>
      )}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '10px', marginBottom: '20px' }}>
        {viewAsUser && user?.role === 'admin' && counts ? (
          <h1 style={{ margin: 0 }}>Viewing Dashboard for: <strong>{counts.targetName || viewAsUser}</strong></h1>
        ) : (
          <h1 style={{ margin: 0 }}>Welcome, {user?.name || user?.username}!</h1>
        )}
        {counts && (
          <span className="table-count" style={{ fontWeight: 'bold', backgroundColor: '#ffeb3b', color: '#212529', fontSize: '1.2rem', padding: '8px 16px', borderRadius: '8px', boxShadow: '0 4px 6px rgba(0,0,0,0.1)' }}>Yesterday's Self IDs: {counts.yesterdaySelfIdCount}</span>
        )}
      </div>
      {renderStats()}
      {renderSelfMembersTable()}
    </div>
  );
};

export default DashboardPage;